/**
 * <soah-slider> — shared behaviour for the theme's horizontal snap carousels.
 *
 * The swipe itself is native CSS scroll-snap. This element adds nothing to it:
 * no drag handler, no pointer capture, no touch interception. That is what
 * keeps a tap on a slide behaving exactly as it would outside a carousel, so
 * media inside a slide keeps its own click and playback behaviour.
 *
 * All it does is mirror the track's scroll position into the arrows and dots,
 * and scroll the track when they are used. No autoplay and no wrap by design:
 * the ends are ends, and the arrows say so.
 *
 * Each section keeps its own markup, class names and CSS, and wires them up
 * through data attributes:
 *
 *   <soah-slider
 *     class="avis-slider"
 *     data-track=".avis-grid"
 *     data-slide=".avis-card"
 *     data-label="Go to review">
 *     <div class="avis-grid" role="region" aria-label="…"> …slides… </div>
 *     <div class="avis-controls">
 *       <button type="button" data-slider-prev aria-label="Previous review">…</button>
 *       <div class="avis-dots" data-slider-dots data-dot-class="avis-dot"></div>
 *       <button type="button" data-slider-next aria-label="Next review">…</button>
 *     </div>
 *   </soah-slider>
 *
 * Every control is optional. With no JS at all — or with the controls omitted —
 * the track is still a native swipeable, keyboard-scrollable region, so this is
 * an enhancement rather than a dependency.
 */
if (!customElements.get('soah-slider')) {
  customElements.define(
    'soah-slider',
    class SoahSlider extends HTMLElement {
      connectedCallback() {
        if (this.ready) return; // the theme editor moves sections; only wire up once
        const track = this.querySelector(this.dataset.track || '[data-slider-track]');
        const slides = track
          ? Array.prototype.slice.call(track.querySelectorAll(this.dataset.slide || '[data-slider-slide]'))
          : [];
        if (!track || slides.length < 2) return;

        this.ready = true;
        this.track = track;
        this.slides = slides;
        this.prev = this.querySelector('[data-slider-prev]');
        this.next = this.querySelector('[data-slider-next]');
        this.dots = this.buildDots();

        if (this.prev) this.prev.addEventListener('click', () => this.go(this.current() - 1));
        if (this.next) this.next.addEventListener('click', () => this.go(this.current() + 1));
        this.track.addEventListener('scroll', () => this.schedule(), { passive: true });
        window.addEventListener('resize', () => this.schedule());
        this.sync();
      }

      // Dots are built from the real slide count, so adding a slide to a
      // section cannot leave a stale row of them behind.
      buildDots() {
        const wrap = this.querySelector('[data-slider-dots]');
        if (!wrap) return [];
        const label = this.dataset.label || 'Go to slide';
        return this.slides.map((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          if (wrap.dataset.dotClass) dot.className = wrap.dataset.dotClass;
          dot.setAttribute('aria-label', label + ' ' + (index + 1));
          dot.addEventListener('click', () => this.go(index));
          wrap.appendChild(dot);
          return dot;
        });
      }

      // One step is the distance between two slides, so the slide width and the
      // gap are read from the layout instead of being restated in JS.
      step() {
        return Math.max(1, this.slides[1].offsetLeft - this.slides[0].offsetLeft);
      }

      current() {
        const index = Math.round(this.track.scrollLeft / this.step());
        return Math.max(0, Math.min(index, this.slides.length - 1));
      }

      go(index) {
        const target = Math.max(0, Math.min(index, this.slides.length - 1));
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({
          left: this.slides[target].offsetLeft - this.slides[0].offsetLeft,
          behavior: reduced ? 'auto' : 'smooth',
        });
      }

      schedule() {
        if (this.pending) return;
        this.pending = requestAnimationFrame(() => {
          this.pending = null;
          this.sync();
        });
      }

      sync() {
        // A scrollable region has to be reachable by keyboard; the same element
        // on desktop is a plain grid and must not steal a tab stop.
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 1;
        if (scrollable) this.track.setAttribute('tabindex', '0');
        else this.track.removeAttribute('tabindex');

        const index = this.current();
        this.dots.forEach((dot, n) => {
          if (n === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        if (this.prev) this.prev.disabled = index === 0;
        if (this.next) this.next.disabled = index === this.slides.length - 1;
      }
    }
  );
}
